/**
 * Copyright © 2026 Delicious AI, LLC
 */

import type { ReviewWorkflow } from '@/graphql'

export type ReviewMode = 'BY_USER' | 'BY_OBJECT'

export const DEFAULT_REVIEW_WORKFLOW: ReviewWorkflow = 'DETECTION'

export type ParsedReviewUrlState = {
  mode: ReviewMode
  workflow: ReviewWorkflow
  email: string | null
  objectId: string | null
  index: number
  startDate: string | null
  endDate: string | null
}

export const getReviewWorkflowFromParam = (
  value: string | null,
): ReviewWorkflow => {
  if (value === 'CLASSIFICATION' || value === 'CORRECTION') {
    return value
  }

  return DEFAULT_REVIEW_WORKFLOW
}

export const parseReviewSearchParams = (
  searchParams: URLSearchParams,
): ParsedReviewUrlState => ({
  mode: searchParams.get('reviewMode') === 'object' ? 'BY_OBJECT' : 'BY_USER',
  workflow: getReviewWorkflowFromParam(
    searchParams.get('workflow') || searchParams.get('objectType'),
  ),
  email: searchParams.get('email'),
  objectId: searchParams.get('objectId'),
  index: Math.max(1, Number(searchParams.get('index') || '1') || 1),
  startDate: searchParams.get('startDate'),
  endDate: searchParams.get('endDate'),
})

export const buildReviewSearchParams = ({
  activeUserEmail,
  email,
  itemIndex,
  mode,
  objectId,
  startDate,
  endDate,
  workflow,
}: {
  activeUserEmail?: string | null
  email: string
  itemIndex?: number | null
  mode: ReviewMode
  objectId: string
  startDate: string | null
  endDate: string | null
  workflow: ReviewWorkflow
}) => {
  const next = new URLSearchParams()
  next.set('reviewMode', mode === 'BY_USER' ? 'user' : 'object')
  next.set('workflow', workflow)

  if (mode === 'BY_USER') {
    const resolvedEmail = activeUserEmail || email.trim()
    if (resolvedEmail) {
      next.set('email', resolvedEmail)
    }
    if (itemIndex) {
      next.set('index', String(itemIndex))
    }
    if (startDate) {
      next.set('startDate', startDate)
    }
    if (endDate) {
      next.set('endDate', endDate)
    }

    return next
  }

  const resolvedObjectId = objectId.trim()
  if (resolvedObjectId) {
    next.set('objectId', resolvedObjectId)
  }

  return next
}
